import { InputWrapper, SelectWrapper } from "@/src/components/tasks/input-wrapper";
import { EntityMatcher } from "@/src/components/tasks/intake-sections/matchers";
import { Hyperlink } from "@/src/components/typography";
import { EnabledFields } from "@/src/lib/schema-types";
import { enumToOptions, getNestedValue, isFieldEnabled } from "@/src/lib/utils/object-utils";
import { type Practitioner, type PractitionerErrors, PractitionerRole } from "@/src/models/practitioner";

interface ProviderFormProps {
  // Base props
  provider: Practitioner | null;
  onChange: (field: keyof Practitioner, value: string) => void;
  createMode?: boolean;

  // practitioner-card-editable props
  errors?: PractitionerErrors;

  // provider-section props
  providerObjString?: string;
  requiredFields?: string[];
  enabledFields?: EnabledFields;
  onSave?: () => void;
  onFocus?: (metadata?: any) => void;
  metadata?: any;

  // ProviderEntityMatcher props
  practitioners?: Practitioner[];
  loading?: boolean;
  onProviderSelected?: (provider: Practitioner | null) => void;
  showProviderMatcher?: boolean;
  onEmptyToggle?: () => void;
}

export default function ProviderForm({
  provider,
  onChange,
  createMode = false,
  errors = {},
  providerObjString,
  requiredFields = [],
  enabledFields,
  onSave,
  onFocus,
  metadata,
  practitioners,
  loading,
  onProviderSelected,
  showProviderMatcher = false,
  onEmptyToggle,
}: ProviderFormProps) {
  const handleFieldChange = (fieldKey: keyof Practitioner, value: string) => {
    onChange(fieldKey, value);
  };

  const getFieldMetadata = (fieldKey: keyof Practitioner) => {
    return getNestedValue(metadata, fieldKey + "_metadata");
  };

  // Check if any provider info fields are enabled
  const hasProviderInfoEnabled = ["givenName", "middleName", "familyName", "specialty", "npiNumber"].some((field) =>
    isFieldEnabled(enabledFields, `${providerObjString}.${field}`)
  );

  // Check if role field is enabled (only relevant in create mode)
  const hasRoleEnabled = createMode && isFieldEnabled(enabledFields, `${providerObjString}.role`);

  const renderField = ({
    fieldKey,
    label,
    type = "text",
    placeholder,
    readOnly = false,
    options,
  }: {
    fieldKey: keyof Practitioner;
    label: string;
    type?: "text" | "email" | "tel" | "select";
    placeholder?: string;
    readOnly?: boolean;
    options?: { value: string; label: string }[];
  }) => {
    const value = getNestedValue(provider, fieldKey) ?? null;
    const error = getNestedValue(errors, fieldKey) ?? null;

    const fullFieldKey = providerObjString ? `${providerObjString}.${fieldKey}` : fieldKey;
    const isRequired = requiredFields.includes(fullFieldKey);

    // Read-only if matched entity
    const isEditable = false;

    if (type === "select" && options) {
      return (
        <div key={fieldKey}>
          <SelectWrapper
            value={value}
            onChange={(newValue) => handleFieldChange(fieldKey, newValue)}
            label={label}
            placeholder={placeholder}
            options={options}
          />
          {error && <p className="text-xs text-red-500 mt-1 ml-1">{error}</p>}
        </div>
      );
    }

    return (
      <div key={fieldKey}>
        <InputWrapper
          type={type}
          value={value}
          onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
          label={label}
          placeholder={placeholder}
          isRequired={isRequired}
          onSave={onSave}
          onFocus={() => onFocus?.(getFieldMetadata(fieldKey))}
          fieldKey={fullFieldKey}
          readOnly={!isEditable}
        />
        {error && <p className="text-xs text-red-500 mt-1 ml-1">{error}</p>}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* ProviderEntityMatcher Section */}
      {(showProviderMatcher || createMode) && onProviderSelected && (
        <div className="flex justify-center items-center mb-4 gap-2">
          <div className="w-[70%]">
            <EntityMatcher
              type="practitioner"
              existingEntities={createMode ? [] : practitioners}
              isLoadingExistingEntities={createMode ? false : loading}
              entity={provider}
              onEntitySelected={onProviderSelected}
              showCreateNewOption={createMode}
            />
          </div>
          {onEmptyToggle && (
            <div className="flex items-center gap-2">
              <Hyperlink onClick={onEmptyToggle} href="#" className="text-xs">
                Empty
              </Hyperlink>
            </div>
          )}
        </div>
      )}

      {/* Provider Information Section */}
      {hasProviderInfoEnabled && (
        <div>
          <h3 className="text-sm font-semibold mb-3 text-muted-foreground">Provider Information</h3>
          <div className="grid grid-cols-3 gap-4">
            {renderField({ fieldKey: "givenName", label: "First Name" })}
            {renderField({ fieldKey: "middleName", label: "Middle Name" })}
            {renderField({ fieldKey: "familyName", label: "Last Name" })}
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4">
            {renderField({ fieldKey: "specialty", label: "Specialty" })}
            {renderField({ fieldKey: "npiNumber", label: "NPI Number", readOnly: true })}
          </div>
          {/* Only render role select when creating a new practitioner */}
          {hasRoleEnabled && (
            <div className="grid grid-cols-1 gap-4 mt-4">
              {renderField({
                fieldKey: "role",
                label: "Role",
                type: "select",
                options: enumToOptions(PractitionerRole).filter(
                  (option) => option.value !== PractitionerRole.UNKNOWN_PROVIDER
                ),
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
